'use client';

import React, { useState, useEffect } from 'react';
import ProjectCard from './ProjectCard';
import FilterBar from './FilterBar';
import ProjectModal from './ProjectModal';
import Icon from '@/components/ui/AppIcon';
import { getPublishedProjects, type ProjectCaseStudy } from '@/services/projectCaseStudyService';

interface ProjectMetric {
  label: string;
  value: string;
  icon: string;
}

interface Project {
  id: string;
  title: string;
  category: string;
  industry: string;
  description: string;
  image: string;
  alt: string;
  metrics: ProjectMetric[];
  tags: string[];
  challenge: string;
  solution: string;
  results: string[];
  timeline: string;
  teamSize: string;
  technologies: string[];
  clientTestimonial?: {
    quote: string;
    author: string;
    position: string;
  };
}

const ProjectsInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsHydrated(true);
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const publishedProjects = await getPublishedProjects();
      
      // Convert database format to component format
      const formattedProjects: Project[] = publishedProjects.map((p) => ({
        id: p.id,
        title: p.title,
        category: p.category.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        industry: p.industry,
        description: p.description,
        image: p.imageUrl || '',
        alt: p.imageAlt || '',
        metrics: p.metrics,
        tags: p.tags,
        challenge: p.challenge,
        solution: p.solution,
        results: p.results,
        timeline: p.timeline || '',
        teamSize: p.teamSize || '',
        technologies: p.technologies,
        clientTestimonial: p.clientTestimonial || undefined,
      }));
      
      setProjects(formattedProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { label: 'All Categories', value: 'all' },
    { label: 'Process Improvement', value: 'process' },
    { label: 'Compliance Initiative', value: 'compliance' },
    { label: 'Team Development', value: 'team' },
    { label: 'Technology Implementation', value: 'technology' },
  ];

  const industries = [
    { label: 'All Industries', value: 'all' },
    { label: 'Pharmaceutical', value: 'pharmaceutical' },
    { label: 'Environmental', value: 'environmental' },
    { label: 'Oil & Gas', value: 'oil-gas' },
    { label: 'Manufacturing', value: 'manufacturing' },
  ];

  const filteredProjects = projects.filter((project) => {
    const matchesCategory =
      selectedCategory === 'all' ||
      project.category.toLowerCase().includes(selectedCategory);
    const matchesIndustry =
      selectedIndustry === 'all' ||
      project.industry.toLowerCase().includes(selectedIndustry);
    const matchesSearch =
      searchQuery === '' ||
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );

    return matchesCategory && matchesIndustry && matchesSearch;
  });

  const handleViewDetails = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  };

  const handleReset = () => {
    setSelectedCategory('all');
    setSelectedIndustry('all');
    setSearchQuery('');
  };

  if (!isHydrated || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-muted rounded-xl" />
              ))}
            </div>
            <div className="h-20 bg-muted rounded-xl" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-96 bg-muted rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="font-heading font-bold text-4xl lg:text-5xl text-text-primary mb-4">
            Projects & Achievements
          </h1>
        </div>

        <FilterBar
          categories={categories}
          industries={industries}
          selectedCategory={selectedCategory}
          selectedIndustry={selectedIndustry}
          searchQuery={searchQuery}
          onCategoryChange={setSelectedCategory}
          onIndustryChange={setSelectedIndustry}
          onSearchChange={setSearchQuery}
          onReset={handleReset}
        />

        {filteredProjects.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="FolderOpenIcon" size={48} className="text-text-secondary" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
              No Projects Found
            </h3>
            <p className="text-text-secondary mb-6">
              Try adjusting your filters or search query to find relevant projects.
            </p>
            <button
              onClick={handleReset}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 transition-all duration-300"
            >
              Reset All Filters
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-text-secondary">
                Showing <span className="font-semibold text-text-primary">{filteredProjects.length}</span> of{' '}
                <span className="font-semibold text-text-primary">{projects.length}</span> projects
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  title={project.title}
                  category={project.category}
                  industry={project.industry}
                  description={project.description}
                  image={project.image}
                  alt={project.alt}
                  metrics={project.metrics}
                  tags={project.tags}
                  onViewDetails={() => handleViewDetails(project)}
                />
              ))}
            </div>
          </>
        )}

        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      </div>
    </div>
  );
};

export default ProjectsInteractive;